/**
 * スケジュール可視性設定サービス
 * FL-114: 陸送員スケジュール公開範囲設定機能
 */

import { getSupabaseClient } from '../utils/supabase';
import logger from '../utils/logger';
import { DriverScheduleVisibility } from '../types';
import { convertDbValueToEnum, convertEnumToDbValue } from '../utils/scheduleVisibilityUtils';

/**
 * 会社のスケジュール可視性設定を取得
 */
export const getCompanyScheduleVisibility = async (
  companyId: string
): Promise<DriverScheduleVisibility> => {
  try {
    logger.info('🔄 スケジュール可視性設定取得開始', { companyId });

    const supabase = getSupabaseClient();
    if (!supabase) {
      throw new Error('Supabaseクライアントが初期化されていません');
    }

    const { data, error } = await supabase
      .from('companies')
      .select('driver_schedule_visibility')
      .eq('id', companyId)
      .single();

    if (error) {
      logger.error('❌ スケジュール可視性設定取得エラー:', error);
      throw error;
    }

    if (!data) {
      logger.warn('⚠️ 会社データが見つかりません', { companyId });
      return DriverScheduleVisibility.ADMIN_ONLY;
    }

    const visibility = data.driver_schedule_visibility || 'admin_only';
    const result = convertDbValueToEnum(visibility);

    logger.info('✅ スケジュール可視性設定取得完了', {
      companyId,
      visibility: result,
    });

    return result;
  } catch (error) {
    logger.error('❌ スケジュール可視性設定取得失敗:', error);
    throw error;
  }
};

/**
 * 会社のスケジュール可視性設定を更新
 */
export const updateCompanyScheduleVisibility = async (
  companyId: string,
  visibility: DriverScheduleVisibility
): Promise<void> => {
  try {
    logger.info('🔄 スケジュール可視性設定更新開始', {
      companyId,
      visibility,
    });

    const dbValue = convertEnumToDbValue(visibility);

    const supabase = getSupabaseClient();
    if (!supabase) {
      throw new Error('Supabaseクライアントが初期化されていません');
    }

    const { data, error } = await supabase
      .from('companies')
      .update({ driver_schedule_visibility: dbValue })
      .eq('id', companyId)
      .select();

    if (error) {
      logger.error('❌ スケジュール可視性設定更新エラー:', error);
      throw error;
    }

    // 更新された行数を確認
    if (!data || data.length === 0) {
      const errorMsg = '更新対象の会社データが見つかりません';
      logger.error('❌ スケジュール可視性設定更新エラー:', { errorMsg, companyId });
      throw new Error(errorMsg);
    }

    logger.info('✅ スケジュール可視性設定更新完了', {
      companyId,
      visibility: dbValue,
      updatedCount: data.length,
      updatedData: data[0],
    });
  } catch (error) {
    logger.error('❌ スケジュール可視性設定更新失敗:', error);
    throw error;
  }
};
